/**
 * Pure helpers backing the invoice Record Payment dialog.
 *
 * The dialog keeps its UI state in the historical {method, reference, amount}
 * shape (the inputs the office types into); the server contract for
 * POST /api/invoices/:id/record-payment uses {payment_method,
 * payment_reference, amount}. These helpers translate UI state into the
 * canonical wire payload so the page component stays a thin renderer and the
 * field-name mapping can be unit-tested without a DOM.
 *
 * Slice 3 — Local payment correctness:
 *   - Before this change, the dialog POSTed {method, reference, amount} and
 *     every Record Payment click returned 400 VALIDATION from the server.
 *   - The server now accepts both shapes for backward compat, but the client
 *     should send the canonical fields going forward.
 */

/** UI state shape held by the React component. */
export interface RecordPaymentFormState {
    method: string;
    /** Free-form check/transaction/zelle reference. Empty string = unset. */
    reference?: string;
    /** Empty string or `undefined` ⇒ server defaults to invoice balance. */
    amount?: number | string;
    /** YYYY-MM-DD date the money was collected. Empty ⇒ server defaults to now(). */
    date?: string;
}

/** Canonical request body for POST /api/invoices/:id/record-payment. */
export interface RecordPaymentRequestBody {
    payment_method: string;
    payment_reference?: string;
    amount?: number;
    paid_at?: string;
}

/**
 * Build the wire payload from form state, mapping UI field names to the
 * server's canonical names and dropping empty/missing optional fields so the
 * server can apply its own defaults (notably: amount = invoice balance).
 *
 * Empty reference → omitted (rather than sent as `""`) so the server doesn't
 * persist a stray empty string into invoice_payments.reference.
 *
 * Missing amount → omitted so the server falls back to `total_amount`.
 */
export function buildRecordPaymentBody(form: RecordPaymentFormState): RecordPaymentRequestBody {
    const body: RecordPaymentRequestBody = {
        payment_method: form.method,
    };
    const trimmedRef = typeof form.reference === "string" ? form.reference.trim() : "";
    if (trimmedRef.length > 0) {
        body.payment_reference = trimmedRef;
    }
    if (form.amount !== undefined && form.amount !== "" && form.amount !== null) {
        const n = typeof form.amount === "number" ? form.amount : Number(form.amount);
        if (Number.isFinite(n) && n > 0) {
            body.amount = n;
        }
    }
    const trimmedDate = typeof form.date === "string" ? form.date.trim() : "";
    if (trimmedDate.length > 0) {
        body.paid_at = trimmedDate;
    }
    return body;
}
